import React from 'react';
import { PropTypes } from 'prop-types';
import {
  Button,
  Divider,
  Image,
  Indicator,
  NumberFormatter,
  Skeleton
} from '@mantine/core';
import { Link } from 'react-router-dom';
import AppStack from '../../../../../common/AppStack';
import AppFlexbox from '../../../../../common/AppFlexbox';
import AppText from '../../../../../common/AppText';
import ProductLineItemAddonsDisplay from '../../../ProductLineItemAddonsDisplay';
import OrderProductsTaxInfo from '../../../../orders/OrderProductsTaxInfo';
import { useMediaQueries } from '../../../../../../helpers/hooks';

const WsAccountOrderSummaryProduct = ({
  mediaQueries,
  baseStoreUrl,
  onNavigate,
  hidePrice,
  loading,
  urlHandle,
  shippingCount,
  count,
  bundleQuantity,
  cancelledCount,
  previewImage,
  productName,
  addOns,
  unitPrice,
  unitTax,
  tax,
  onViewTracking,
  trackingUrl,
  bundleProducts,
  isCancelledList,
  isBundleItem
}) => {
  const { isTabletOrSmaller, getResponsiveStyle: rs } =
    mediaQueries || useMediaQueries();
  const url = !isBundleItem ? `${baseStoreUrl}?urlHandle=${urlHandle}` : null;

  const statusInfo =
    shippingCount > 0
      ? {
          label:
            shippingCount >= count - cancelledCount
              ? 'Shipped'
              : `(${shippingCount}/${count}) Shipped`,
          color: 'dodgerblue'
        }
      : cancelledCount >= count
      ? {
          label: 'Cancelled',
          color: '#C40000'
        }
      : {
          label: 'Processing',
          color: '#000'
        };

  const itemCount = bundleQuantity ?? count;

  const productPrice = (unitPrice - (unitTax ?? 0)) * count;

  return !loading ? (
    <AppFlexbox
      style={{
        flex: 1,
        flexWrap: 'nowrap',
        flexDirection: rs(['column', 'column', 'column', 'row']),
        gap: 16,
        justifyContent: 'space-between'
      }}
    >
      <AppStack style={{ gap: 8, flex: 1 }}>
        <AppText
          style={
            isBundleItem
              ? { fontSize: 14, fontWeight: 500, color: statusInfo.color }
              : { fontSize: 18, fontWeight: 700, color: statusInfo.color }
          }
        >
          {statusInfo.label}
        </AppText>
        <AppFlexbox
          style={{
            flex: 1,
            flexWrap: 'nowrap',
            flexDirection: isTabletOrSmaller ? 'column' : 'row'
          }}
        >
          <AppFlexbox
            style={{ flexWrap: 'nowrap', alignSelf: 'stretch', flex: 2 }}
          >
            <AppFlexbox style={{ alignSelf: 'start', flex: 1 }}>
              <Indicator
                color="dodgerblue"
                disabled={!itemCount || itemCount <= 1}
                label={itemCount}
                size={isBundleItem ? 16 : 20}
              >
                <AppStack
                  component={url ? Link : null}
                  onClick={onNavigate}
                  style={{
                    border: 'solid 1px lightgrey',
                    borderRadius: 8,
                    position: 'relative',
                    width: isBundleItem
                      ? isTabletOrSmaller
                        ? 40
                        : 60
                      : isTabletOrSmaller
                      ? 70
                      : 100,
                    backgroundColor: '#f2f2f2',
                    overflow: 'hidden',
                    aspectRatio: '1 / 1'
                  }}
                  to={url}
                >
                  <Image
                    fit="cover"
                    h="100%"
                    loading="lazy"
                    src={previewImage}
                    style={{ position: 'absolute' }}
                    w="100%"
                  />
                </AppStack>
              </Indicator>

              <AppStack
                style={{
                  flex: 1,
                  gap: 0
                }}
              >
                <AppStack
                  component={url ? Link : null}
                  onClick={onNavigate}
                  style={{
                    gap: 0,
                    textDecoration: 'none',
                    color: '#000'
                  }}
                  to={url}
                >
                  <AppText
                    style={
                      isBundleItem
                        ? { fontSize: 14, fontWeight: 500 }
                        : { fontSize: 18, fontWeight: 700 }
                    }
                  >
                    {productName}
                  </AppText>

                  {(!bundleProducts || bundleProducts.length === 0) && (
                    <ProductLineItemAddonsDisplay
                      addOns={addOns}
                      showVariantOptionsOnSingleLine
                    />
                  )}
                </AppStack>

                {bundleProducts &&
                  bundleProducts.length > 0 &&
                  bundleProducts.map((p, index) => (
                    <React.Fragment key={p.key}>
                      {index > 0 && <Divider />}
                      <AppFlexbox style={{ padding: 8 }}>
                        <WsAccountOrderSummaryProduct
                          addOns={p.addOns}
                          baseStoreUrl={baseStoreUrl}
                          hidePrice
                          isBundleItem
                          mediaQueries={mediaQueries}
                          onNavigate={onNavigate}
                          previewImage={p.previewImage}
                          productName={p.name}
                          urlHandle={p.productUrlHandle}
                          {...(isCancelledList
                            ? {
                                cancelledCount: p.cancelledCount,
                                count: p.cancelledCount,
                                shippingCount: 0
                              }
                            : {
                                cancelledCount: 0,
                                count: p.quantity,
                                bundleQuantity: p.bundleQuantity,
                                onViewTracking: onViewTracking
                                  ? () => onViewTracking(p)
                                  : undefined,
                                shippingCount: p.shippedCount
                              })}
                        />
                      </AppFlexbox>
                    </React.Fragment>
                  ))}
              </AppStack>
            </AppFlexbox>
          </AppFlexbox>

          {!hidePrice && (
            <AppStack
              style={{
                alignSelf: 'start',
                placeItems: 'end',
                marginLeft: 'auto',
                gap: 10
              }}
            >
              <AppStack style={{ gap: 0, alignItems: 'end' }}>
                <AppText style={{ fontSize: 18 }} weight={700}>
                  <NumberFormatter
                    decimalScale={2}
                    displayType="text"
                    fixedDecimalScale
                    prefix="$"
                    thousandSeparator
                    value={productPrice / 100}
                  />
                </AppText>

                {count > 1 && (
                  <AppText style={{ fontSize: 14, color: '#666' }}>
                    <NumberFormatter
                      decimalScale={2}
                      displayType="text"
                      fixedDecimalScale
                      prefix="$"
                      suffix={` each`}
                      thousandSeparator
                      value={(unitPrice - (unitTax ?? 0)) / 100}
                    />
                  </AppText>
                )}
                {tax &&
                  tax.length > 0 &&
                  (bundleProducts && bundleProducts.length > 0 ? (
                    <OrderProductsTaxInfo taxItems={tax} />
                  ) : (
                    <OrderProductsTaxInfo
                      taxItems={[
                        {
                          entity: 1,
                          tax: tax.map((t) => ({
                            name: t.name,
                            taxRate: t.taxRate,
                            taxAmount: t.taxInCents
                          }))
                        }
                      ]}
                    />
                  ))}
              </AppStack>
            </AppStack>
          )}
        </AppFlexbox>
      </AppStack>

      <AppStack
        style={{
          alignSelf: 'start',
          placeItems: 'end',
          marginLeft: 'auto',
          gap: rs([0, 20]),
          flexDirection: rs([
            'row-reverse',
            'row-reverse',
            'row-reverse',
            'column'
          ]),
          justifyContent: rs([
            'space-between',
            'space-between',
            'space-between',
            'inital'
          ]),
          width: rs(['100%', '100%', '100%', 'unset'])
        }}
      >
        <AppStack style={{ gap: 0, alignItems: 'end' }}>
          {shippingCount > 0 && (onViewTracking || trackingUrl) && (
            <Button
              color="blue"
              component={trackingUrl ? Link : null}
              onClick={(e) => {
                if (trackingUrl) {
                  onNavigate(e);
                }
                else if (onViewTracking) {
                  onViewTracking();
                }
              }}
              size="compact-md"
              to={trackingUrl}
              variant="outline"
            >
              Track package
            </Button>
          )}
        </AppStack>
      </AppStack>
    </AppFlexbox>
  ) : (
    <AppFlexbox
      style={{
        flexWrap: 'nowrap',
        flexDirection: rs(['column', 'column', 'column', 'row'])
      }}
    >
      <AppFlexbox style={{ flexWrap: 'nowrap', flex: 2, alignSelf: 'stretch' }}>
        <Skeleton
          height={isTabletOrSmaller ? 70 : 100}
          width={isTabletOrSmaller ? 70 : 100}
        />
        <AppStack
          style={{
            flex: 1,
            gap: 10,
            textDecoration: 'none',
            color: '#000'
          }}
        >
          <Skeleton height={12} width="50%" />
          <Skeleton height={12} width="40%" />
        </AppStack>
      </AppFlexbox>

      <AppStack
        style={{
          flex: 1,
          alignSelf: 'start',
          placeItems: 'end',
          marginLeft: 'auto',
          gap: rs([0, 0, 0, 30]),
          flexDirection: rs([
            'row-reverse',
            'row-reverse',
            'row-reverse',
            'column'
          ]),
          justifyContent: rs([
            'space-between',
            'space-between',
            'space-between',
            'inital'
          ]),
          width: rs(['100%', '100%', '100%', 'unset'])
        }}
      >
        <AppStack style={{ gap: 0, alignItems: 'end' }}>
          <Skeleton height={20} width={100} />
        </AppStack>
      </AppStack>
    </AppFlexbox>
  );
};

WsAccountOrderSummaryProduct.propTypes = {
  addOns: PropTypes.array,
  baseStoreUrl: PropTypes.string,
  bundleProducts: PropTypes.array,
  bundleQuantity: PropTypes.number,
  cancelledCount: PropTypes.number,
  count: PropTypes.number,
  hidePrice: PropTypes.bool,
  isBundleItem: PropTypes.bool,
  isCancelledList: PropTypes.bool,
  loading: PropTypes.bool,
  mediaQueries: PropTypes.object,
  onNavigate: PropTypes.func,
  onViewTracking: PropTypes.func,
  previewImage: PropTypes.string,
  productName: PropTypes.string,
  shippingCount: PropTypes.number,
  tax: PropTypes.array,
  taxRate: PropTypes.number,
  trackingUrl: PropTypes.string,
  unitPrice: PropTypes.number,
  unitTax: PropTypes.number,
  urlHandle: PropTypes.string
};

export default WsAccountOrderSummaryProduct;
